import { useState, useEffect } from "react";
import PropTypes from 'prop-types';

// @mui material components
import { Grid, Box, Card } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

//Backend access
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth0 } from "@auth0/auth0-react";

import bksAPI from "services/api/bks";
import { wrapper, loadingScreen } from "utils/wrapper";
import { useSoftUIController } from "hooks/context";

import { disableMap } from "utils/map";
import SoftButton from "components/SoftComponents/SoftButton";
import LiveView from "components/LiveView";

import LiveviewTimeline from "components/Timelines/LiveviewTimeline";
import { processDevicesData } from "utils/device";
import SoftTypography from "components/SoftComponents/SoftTypography";

function TfNSWMultiView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    bksAPI.loadToken(useAuth0());

    const navigate = useNavigate();

    const [isPerspectiveLiveview, setIsPerspectiveLiveview] = useState(true);
    const [directionTrigger, setDirectionTrigger] = useState({ prev: 0, next: 0 });

    var devices_data = null;
    const devices = useQuery({
        queryKey: ['getDevices', ['310019-3432510f-39323938', '3a0030-31335109-30313433', '340037-31335109-30313433'].join(','), selectedOrg],
        queryFn: async () => {
            return await bksAPI.getDevices(['310019-3432510f-39323938', '3a0030-31335109-30313433', '340037-31335109-30313433']);
        },
        refetchInterval: 15 * 1000
    });

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Prevent arrows from scrolling the page
            if (e.keyCode === 37 || e.keyCode === 39) {
                if (e.keyCode === 39) { // Right
                    setDirectionTrigger(prev => ({ ...prev, next: prev.next + 1 }));
                } else if (e.keyCode === 37) { // Left
                    setDirectionTrigger(prev => ({ ...prev, prev: prev.prev + 1 }));
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, []);

    useEffect(() => {
        const deviceMissing = !devices.isLoading && !devices.data;

        if (deviceMissing || selectedOrg != 'tfnsw') {
            navigate('/devices', { replace: true });
        } 

    }, [devices.data, devices.isLoading, navigate]);

    if (devices.isLoading) {
        return loadingScreen(controller, true);
    }

    if (devices.error) {
        return wrapper("An error has occurred fetching data", controller);
    }

    const device_map = {
        '310019-3432510f-39323938': 0,
        '3a0030-31335109-30313433': 1,
        '340037-31335109-30313433': 2
    }

    devices_data = devices.data && processDevicesData(devices.dataUpdatedAt, devices.data).sort((a, b) => device_map[b.uuid] - device_map[a.uuid]);

    if (!devices.isLoading && devices.data) {
        const liveviewInfo = (dev, idx) => (
            <Box display="flex" flex={1} flexDirection={{ xxs: "column" }} height="calc(100svh - 10.25rem - 16px)"> {/* Need to fix for mobile state */}
                {(dev?.overlay_id && dev.overlay_id != 'no_frame') ? (
                    <>
                        <Box flex={{ xxs: 1 }} pb={1}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                <Card sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' ,height: '80px'}}>
                                    <SoftTypography px={2} variant="h5" fontWeight="bold" sx={{textAlign: 'center'}}>{idx == 0 ? 'Field Device' : dev.name}</SoftTypography>
                                </Card>
                            </SoftBox>
                        </Box>
                        <Box flex={{ xxs: 1 }} pb={1}>
                            <SoftBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                {(idx == 1 && dev?.overlay_id && dev?.overlay_id != 'no_frame') && (
                                    <SoftButton variant="contained" color="dark" circular={true} onClick={() => { setIsPerspectiveLiveview(!isPerspectiveLiveview) }}
                                        sx={{ fontSize: '13px', mx: 1, minHeight: '32px !important' }}>
                                        Switch Views
                                    </SoftButton>
                                )}
                            </SoftBox>
                        </Box>
                        <Box display="flex" flex={{ xxs: 10 }} sx={{ maxHeight: 'calc(100svh - 21rem - 16px)' }}>
                            <LiveView device={dev} nexus_id={user.organisations.find((org) => org.organisation_id == dev.organisation_id).nexus_id || null} selectedOrg={selectedOrg} isPerspectiveLiveview={dev?.overlay_id && dev?.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                        </Box>
                    </>
                ) : (
                    <Box display="flex" alignItems="center" flex={{ xxs: 12 }} sx={{ maxHeight: 'calc(100svh - 10.25rem - 16px)' }}>
                        <LiveView device={dev} nexus_id={user.organisations.find((org) => org.organisation_id == dev.organisation_id).nexus_id || null} selectedOrg={selectedOrg} isPerspectiveLiveview={dev?.overlay_id && dev.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                    </Box>
                )}
            </Box>
        );

        return wrapper((
            <SoftBox mt={1} sx={{ overflow: "visible", backgroundColor: 'transparent', height: '100%' }}>
                <Grid container spacing={1} sx={{ px: 1, height: '100%' }}>
                    {devices_data.map((dev, idx) => (
                        <Grid item xxs={4} key={idx}>
                            <Box pb={1} sx={{ height: 'calc(100% - 8px)' }}>
                                {liveviewInfo(dev, idx)}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </SoftBox>
        ), controller, true);
    }
}

TfNSWMultiView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default TfNSWMultiView;
